import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  ControlGroupRegisterResponse,
  ControlGroupRegisterResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetControlGroupsFlatQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcControlGroupToGraphQL = (
  controlGroup: ControlGroupRegisterResponseRow
): GetControlGroupsFlatQuery['control_group'][number] => {
  return {
    ...controlGroup,
    controls_aggregate: {
      aggregate: {
        count: controlGroup.controls.length,
      },
    },
  };
};

/**
 * Maps TRPC control data to match the GraphQL query structure
 */
const mapTrpcControlGroupsToGraphQL = (
  trpcData: ControlGroupRegisterResponse
): GetControlGroupsFlatQuery => {
  return {
    control_group: trpcData.control_group.map((control) =>
      mapTrpcControlGroupToGraphQL(control)
    ),
  };
};

export const useGetControlGroupsRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.controlGroup.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcControlGroupsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
